import { RefObject, useEffect, useRef, useState } from 'react'

import { InnosErrorBoundary } from '@inno-tools/container-bridge'
import { ContextMenuRef } from '@inno-ui/context-menu'
import { OnboardingTour, useOnboardingTour } from '@inno-ui/onboarding-tour'
import cn from 'classnames'
import {
  AgentChatLauncherApp,
  AppMetaTag,
  ClusterFoneCallWidgetApp
} from 'modules/app-registry/components'
import { NotificationApp } from 'modules/app-registry/components/NotificationApp'
import { hasAppAccess } from 'modules/app-registry/hooks/hasAppAccess'
import { useLogout } from 'modules/auth/hooks/useLogout'
import {
  ProductTourModal,
  ProductUpdates,
  TwoFaReminderModal
} from 'modules/desktop/components'
import { OnboardingIntro } from 'modules/desktop/modules/onboarding'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useGetPartnerResponsiblesQuery } from 'shared/api/afterSalesApi'
import { useGetAppsQuery } from 'shared/api/containerApi/requests'
import { AgentBridgeManager } from 'shared/components/AgentBridgeManager'
import { AuthBroadcastMessages } from 'shared/constants/broadcast/auth'
import { BroadcastChannels } from 'shared/constants/broadcast/channels'
import { CustomEvents } from 'shared/constants/customEvents'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { useIsSffOrg } from 'shared/hooks/useIsSffOrg'
import { useLocalStorage } from 'shared/hooks/useLocalStorage'
import { TABLET_WIDTH, useMediaQuery } from 'shared/hooks/useMediaQuery'
import { useNotificationBadges } from 'shared/hooks/useNotificationBadges'
import { USER_MENU_TOUR_ID, useOnboarding } from 'shared/hooks/useOnboarding'
import { useOpenWindowEventListener } from 'shared/hooks/useOpenWindowEventListener'
import { usePageFullyLoaded } from 'shared/hooks/usePageFullyLoaded'
import { useProductUpdatesSocket } from 'shared/hooks/useProductUpdatesSocket'
import { useRefreshToken } from 'shared/hooks/useRefreshToken'
import { useTokenRefreshSocket } from 'shared/hooks/useTokenRefreshSocket'
import { useUserContext } from 'shared/hooks/useUserContext'
import { useLaunchpadStore } from 'shared/stores/launchpadStore'
import { AppInstance } from 'shared/types'
import { getIsInsideWindow } from 'shared/utils'
import { createTypedBroadcastChannel } from 'shared/utils/broadcastChannel'
import { listenCustomEvent } from 'shared/utils/customEvents'
import { lsGetItem } from 'shared/utils/local-storage/lsGetItem'
import { useCommonStore } from 'src/shared/stores/commonStore'
import { useWallpaperStore } from 'src/shared/stores/wallpaperStore'

import styles from './MainLayout.module.scss'
import {
  getRoleOverviewRoles,
  hasInnoscriptaChampionResponsibility
} from './MainLayout.utils'
import {
  FullPageOnboarding,
  FullTour,
  NewGrantApplicationModal,
  PostOnboardingScreen,
  QuickActions,
  ReleaseNotesModal,
  SubmitRequestModal,
  TaskModal,
  TicketModal,
  TrackTicketsModal,
  UniversalSearch
} from './components'
import { AnimatedWallpaper } from './components/AnimatedWallpaper/AnimatedWallpaper'
import { LaunchPad } from './components/LaunchPad'
import {
  AppLocationType,
  CommonDockProps
} from './components/LaunchPad/LaunchPad.types'
import { TopDockDesktop, TopDockMobile } from './components/TopDock'
import { useGetAllDesktopSettings } from './hooks/useGetAllDesktopSettings'
import { useGetDemoOrgIds } from './hooks/useGetDemoOrgIds'
import { useMeetingNavigationGuard } from './hooks/useMeetingNavigationGuard'

interface Props {
  children?: React.ReactNode
  contentAreaRef?: React.RefObject<HTMLDivElement>
  handleAppClick?: (
    app: AppInstance,
    type: AppLocationType | 'universalSearch'
  ) => void
  topDockIconRefs?: CommonDockProps['iconRefs']
  launchpadIconRefs?: CommonDockProps['iconRefs']
  topDockHeight: number
  topDockRef:
    | RefObject<HTMLDivElement>
    | ((instance: HTMLDivElement | null) => void)
    | null
  quickActions?: React.ReactNode
}

export const MainLayout: React.FC<Props> = ({
  children,
  contentAreaRef,
  handleAppClick,
  topDockIconRefs,
  launchpadIconRefs,
  topDockHeight,
  topDockRef,
  quickActions
}) => {
  // Install the shared navigation bridge so meeting-mode navigation is
  // intercepted even when the current page does not otherwise consume history.
  useMeetingNavigationGuard()
  useOpenWindowEventListener()

  const { width } = useMediaQuery()
  const isMobile = width < TABLET_WIDTH

  /**
   *  This variable  is here to check if the app is running inside a window/iframe
   *  The `isDetached` global variable and the `detached` prop have different meanings from each other.
   *
   *  - The `detached` prop is used to determine if the app is running in a standalone mode
   *  - The `isDetached` global variable is used to determine if the app is running inside a window/iframe
   */

  const { user } = useUserContext()
  const isSffOrg = useIsSffOrg()
  const {
    data: partnerResponsibles,
    isError: isPartnerResponsiblesError,
    isFetched: arePartnerResponsiblesFetched
  } = useGetPartnerResponsiblesQuery({ enabled: isSffOrg })

  const isInnoscriptaChampion =
    !isPartnerResponsiblesError &&
    hasInnoscriptaChampionResponsibility(user?.id, partnerResponsibles)

  const roleOverviewRoles = getRoleOverviewRoles(user?.roles)
  const hasRoleOverview =
    arePartnerResponsiblesFetched &&
    !isPartnerResponsiblesError &&
    !isInnoscriptaChampion &&
    roleOverviewRoles.length > 0

  const fullPageOnboardingVariant = isInnoscriptaChampion
    ? 'champion'
    : hasRoleOverview
      ? 'role-overview'
      : null

  const isFullPageOnboardingEnabled =
    fullPageOnboardingVariant !== null &&
    FEATURE_FLAGS.FULL_PAGE_ONBOARDING({
      organizationId: user?.organization_id ?? -1,
      isSffOrg
    })

  const isInsideWindow = getIsInsideWindow()

  const setTopDockHeight = useCommonStore(state => state.setTopDockHeight)
  const isDetached = useCommonStore(state => state.isDetached)
  const setIsClusterMeetOnline = useCommonStore(
    state => state.setIsClusterMeetOnline
  )
  const currentWallpaper = useWallpaperStore(state => state.currentWallpaper)
  const setIsLaunchpadOpen = useLaunchpadStore(
    state => state.setIsLaunchpadOpen
  )

  const logout = useLogout()
  useRefreshToken()
  useTokenRefreshSocket()
  useProductUpdatesSocket()

  const { t } = useTranslation('translation', {
    keyPrefix: 'onboarding'
  })

  useGetAllDesktopSettings()
  useGetDemoOrgIds()
  useNotificationBadges()

  useEffect(() => {
    return listenCustomEvent(
      CustomEvents.CLUSTER_MEET_STATE_CHANGED,
      ({ isOnline }) => setIsClusterMeetOnline(isOnline),
      window
    )
  }, [setIsClusterMeetOnline])

  const { minimizeAllWindows } = useWindowStore()
  const restoreWindowsRef = useRef<(() => void) | null>(null)
  const topDockLayerRef = useRef<HTMLDivElement>(null)
  const inventoryAppContextMenuRef = useRef<ContextMenuRef>(null)
  const [hasCompletedTour, setHasCompletedTour] = useState(
    Boolean(lsGetItem(LocalStorageKey.COMPLETED_TOURS)?.[USER_MENU_TOUR_ID])
  )
  const [showPostOnboardingScreen, setShowPostOnboardingScreen] =
    useState(false)
  const [showFullTour, setShowFullTour] = useState(false)
  const [isFullPageOnboardingDismissed, setIsFullPageOnboardingDismissed] =
    useState(false)
  const [isFullPageOnboardingCompleted, setIsFullPageOnboardingCompleted] =
    useLocalStorage(
      LocalStorageKey.COMPLETED_TOURS,
      'full-page-onboarding',
      false
    )
  const [isAgentWidgetVisible] = useLocalStorage(
    LocalStorageKey.AGENT_CHAT_WIDGET_VISIBLE,
    true
  )
  const { data: containerAppsData = [] } = useGetAppsQuery()
  const isPageFullyLoaded = usePageFullyLoaded()
  const isOnboardingPausedRef = useRef(false)
  const activeOnboardingOverlay =
    isFullPageOnboardingEnabled && !isMobile
      ? !isFullPageOnboardingCompleted && !isFullPageOnboardingDismissed
        ? 'full'
        : showPostOnboardingScreen
          ? 'post'
          : null
      : null
  const shouldRenderDesktopShell =
    !showFullTour &&
    (isFullPageOnboardingCompleted ||
      isFullPageOnboardingDismissed ||
      !isFullPageOnboardingEnabled ||
      isMobile)

  useEffect(() => {
    if (activeOnboardingOverlay === 'post') {
      restoreWindowsRef.current = minimizeAllWindows()
    }
  }, [activeOnboardingOverlay, minimizeAllWindows])

  useEffect(() => {
    const topDockLayer = topDockLayerRef.current
    if (!topDockLayer) return

    topDockLayer.toggleAttribute('inert', activeOnboardingOverlay === 'full')
  }, [activeOnboardingOverlay])

  const { tourId, tourSteps } = useOnboarding({
    launchpadIconRefs,
    inventoryAppContextMenuRef
  })

  const {
    tourRef,
    startTour,
    closeTour,
    onStepChange,
    continueTour,
    onTourPause,
    onComplete
  } = useOnboardingTour({
    onTourPause: () => {
      isOnboardingPausedRef.current = true
    },
    onComplete: () => {
      const event = new Event(CustomEvents.ONBOARDING_TOUR_COMPLETED)
      document.dispatchEvent(event)
    }
  })

  const handleStartTour = () => {
    setHasCompletedTour(true)
    startTour()
  }
  const handleSkipTour = () => {
    setHasCompletedTour(true)
    closeTour()
  }

  const handleOpenFullTour = () => {
    setIsFullPageOnboardingDismissed(true)
    setShowFullTour(true)
  }

  const handleCompleteFullPageOnboarding = () => {
    setShowPostOnboardingScreen(fullPageOnboardingVariant === 'champion')
    setIsFullPageOnboardingCompleted(true)
  }

  const handleAnimationCompleted = () => {
    if (isOnboardingPausedRef.current) {
      continueTour()
      isOnboardingPausedRef.current = false
    }
  }

  useEffect(() => {
    if (isInsideWindow || !topDockHeight) return
    setTopDockHeight(topDockHeight)
    document.documentElement.style.setProperty(
      '--container-top-dock-height',
      `${topDockHeight}px`
    )
  }, [topDockHeight, isInsideWindow, setTopDockHeight])

  useEffect(() => {
    const handleAppLoggedOut = () => {
      void logout('received_app_logged_out_event')
    }

    window.addEventListener(CustomEvents.APP_LOGGED_OUT, handleAppLoggedOut)

    return () => {
      window.removeEventListener(
        CustomEvents.APP_LOGGED_OUT,
        handleAppLoggedOut
      )
    }
  }, [logout])

  useEffect(() => {
    const authChannel = createTypedBroadcastChannel(BroadcastChannels.AUTH)
    const unsubscribe = authChannel.subscribe(message => {
      if (message.type === AuthBroadcastMessages.LOGOUT) {
        void logout()
      }
    })

    return () => {
      unsubscribe()
      authChannel.close()
    }
  }, [logout])

  function handleLogoClick() {
    minimizeAllWindows()
    setIsLaunchpadOpen(false)
  }

  if (!isInsideWindow) {
    return (
      <>
        <AgentBridgeManager />
        <TwoFaReminderModal />
        <ProductTourModal />
        <ReleaseNotesModal />
        <AppMetaTag />
        <motion.div
          style={{
            backgroundImage: currentWallpaper.image
              ? `url("${currentWallpaper.image}")`
              : undefined
          }}
          className={cn(styles.mainLayout, {
            [styles.detached]: isInsideWindow || isDetached
          })}
        >
          {!isDetached && <AnimatedWallpaper />}
          <AnimatePresence mode='wait'>
            {activeOnboardingOverlay === 'full' && isPageFullyLoaded && (
              <motion.div
                key='full-page-onboarding'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.32, ease: 'easeInOut' }}
              >
                {fullPageOnboardingVariant === 'role-overview' ? (
                  <FullPageOnboarding
                    variant='role-overview'
                    roles={roleOverviewRoles}
                    userName={
                      user?.profile?.f_name?.trim() ||
                      user?.name?.trim().split(/\s+/)[0] ||
                      user?.username ||
                      ''
                    }
                    onComplete={handleCompleteFullPageOnboarding}
                  />
                ) : (
                  <FullPageOnboarding
                    variant='champion'
                    onComplete={handleCompleteFullPageOnboarding}
                    onOpenFullTour={handleOpenFullTour}
                  />
                )}
              </motion.div>
            )}
            {activeOnboardingOverlay === 'post' && (
              <motion.div
                key='post-onboarding-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: 'easeInOut' }}
              >
                <PostOnboardingScreen
                  onClose={() => {
                    restoreWindowsRef.current?.()
                    setShowPostOnboardingScreen(false)
                  }}
                />
              </motion.div>
            )}
            {showFullTour && (
              <motion.div
                key='full-tour'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.32, ease: 'easeInOut' }}
              >
                <FullTour
                  onComplete={() => setShowFullTour(false)}
                  onSkip={() => setShowFullTour(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>
          <div
            ref={topDockLayerRef}
            className={styles.topDockLayer}
            aria-hidden={activeOnboardingOverlay === 'full' || undefined}
          >
            {isMobile ? (
              <TopDockMobile handleLogoClick={handleLogoClick} />
            ) : (
              <TopDockDesktop
                handleLogoClick={handleLogoClick}
                handleAppClick={handleAppClick}
                iconRefs={topDockIconRefs}
                ref={topDockRef}
              />
            )}
          </div>
          {shouldRenderDesktopShell && (
            <>
              <OnboardingTour
                finishButtonText={t('navigation.finish')}
                nextButtonText={t('navigation.next')}
                prevButtonText={t('navigation.previous')}
                tourId={tourId}
                steps={tourSteps}
                onTourPause={onTourPause}
                autoStart={false}
                ref={tourRef}
                hasGuide={false}
                onStepChange={onStepChange}
                onComplete={() => {
                  setHasCompletedTour(true)
                  onComplete?.()
                }}
              />
              <OnboardingIntro
                handleStartTour={handleStartTour}
                handleSkipTour={handleSkipTour}
                // TODO: Remove || true once flow is fixed
                hasCompletedTour={hasCompletedTour || true}
              />

              <LaunchPad
                inventoryAppContextMenuRef={inventoryAppContextMenuRef}
                handleAppClick={handleAppClick}
                iconRefs={launchpadIconRefs}
                onAnimationComplete={handleAnimationCompleted}
              />

              <InnosErrorBoundary debugName='NotificationApp'>
                <NotificationApp />
              </InnosErrorBoundary>
              <InnosErrorBoundary debugName='TicketModal'>
                <TicketModal />
              </InnosErrorBoundary>
              <InnosErrorBoundary debugName='TaskModal'>
                <TaskModal />
              </InnosErrorBoundary>
              <InnosErrorBoundary debugName='TrackTicketsModal'>
                <TrackTicketsModal />
              </InnosErrorBoundary>
              <InnosErrorBoundary debugName='SubmitRequestModal'>
                <SubmitRequestModal />
              </InnosErrorBoundary>
              <InnosErrorBoundary debugName='NewGrantApplicationModal'>
                <NewGrantApplicationModal />
              </InnosErrorBoundary>
              {!isMobile &&
                hasAppAccess('agent', containerAppsData) &&
                isAgentWidgetVisible && (
                  <InnosErrorBoundary debugName='AgentChatLauncherApp'>
                    <AgentChatLauncherApp />
                  </InnosErrorBoundary>
                )}
              {isPageFullyLoaded && (
                <InnosErrorBoundary debugName='ClusterFoneCallWidgetApp'>
                  <ClusterFoneCallWidgetApp />
                </InnosErrorBoundary>
              )}
              <QuickActions>{quickActions}</QuickActions>
              <UniversalSearch handleAppClick={handleAppClick} />
              <InnosErrorBoundary debugName='ProductUpdates'>
                <ProductUpdates />
              </InnosErrorBoundary>

              <div ref={contentAreaRef} className={styles.contentArea}>
                {children}
              </div>
            </>
          )}
        </motion.div>
      </>
    )
  }

  return children
}
